import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { CacheObjectService, DeviceService, EventTrackerService } from '@app/core';
import { AppParametersService } from '@app/core/token/app-parameters.service';
import { CheckHtml5Service } from '@app/helper/check-html5.service';
import { ScrollToLastPosition } from '@app/helper/scroll-to-last-position';
import { ProductTypeEnum } from '@app/interfaces/iBook';
import { iGenre } from '@app/interfaces/IGenre';
import { IndicatorService } from '@app/shared';
import { delay, filter } from 'rxjs/operators';

import { CLPService } from '../clp.service';
import {environment} from "@env/environment";

export const listAnimation = trigger("listAnimation", [
  transition("* => *", [
    query(
      ":enter",
      [
        style({ opacity: 0 }),
        stagger(30, [animate("0.06s", style({ opacity: 1 }))]),
      ],
      { optional: true }
    ),
  ]),
]);

@Component({
  selector: "navaar-clp",
  templateUrl: "./clp.component.html",
  styleUrls: ["./clp.component.scss"],
  animations: [listAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CLPComponent extends ScrollToLastPosition implements OnInit {
  public productType: typeof ProductTypeEnum = ProductTypeEnum;
  public selectedTab: number = 0;
  public genres: Array<iGenre>;
  public items: Array<iGenre> = [];
  public showOldVersion: boolean = false;
  public environment = environment;

  constructor(
    private readonly parametersService: AppParametersService,
    private readonly trackerEvent: EventTrackerService,
    private readonly checkHtml5Service: CheckHtml5Service,
    private readonly chkd: ChangeDetectorRef,
    public readonly clpService: CLPService,
    private readonly cache: CacheObjectService<Array<iGenre>>,
    private readonly router: Router,
    private readonly indicator: IndicatorService,
    public readonly device: DeviceService) {
    super(router);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        if (event.restoredState) this.clpService.isBackFromItem = true;
      });
    this.device.hide();
    this.indicator.show();
  }

  ngOnInit() {
    this.checkSupportHtml();
    const clientId = this.parametersService.clientId;
    this.cache
      .getObjectCache(`/api/genres?clientId=${clientId}`)
      .subscribe((c) => {
        this.clpService.setGenres(c);
        this.genres = c.map(
          (z, i) =>
            <iGenre>{
              genreId: z.genreId,
              identifier: z.identifier,
              name: z.name,
              contentType: z.contentType,
              subGenres: z.subGenres.map(
                (x) =>
                  <iGenre>{
                    genreId: x.genreId,
                    identifier: x.identifier,
                    name: x.name,
                    imageUrl: x.imageUrl,
                    contentType: z.contentType,
                  }
              )
            }
        );

        this.items = this.genres[0].subGenres;
        this.indicator.hide();
        if (this.clpService.isBackFromItem && this.clpService.selectTab)
          this.selectedCategory(
            this.clpService.selectTab.identifier,
            this.clpService.selectTab.indexTab,
          );
        this.chkd.detectChanges();
      });
  }
  checkSupportHtml() {
    let supportVideo = this.checkHtml5Service.supports_video();
    let supportsCanvas = this.checkHtml5Service.supports_canvas();
    if (!supportVideo) {
      this.showOldVersion = true;
    }
    if (!supportsCanvas) {
      this.showOldVersion = true;
    }
  }
  public async selectedCategory(identifier, index) {
    this.clpService.selectTab = { identifier, indexTab: index };
    this.selectedTab = index;
    this.items = [];
    this.items = this.genres
      .find((c) => c.identifier == identifier)
      .subGenres.map(
        (z) =>
          <iGenre>{
            genreId: z.genreId,
            identifier: z.identifier,
            name: z.name,
            imageUrl: z.imageUrl,
            contentType: z.contentType,
          });
    if (this.clpService.isBackFromItem) {
      await delay(100);
      super.scrollToPosition();
      this.clpService.isBackFromItem = false;
    }
    else {
      this.trackerEvents({
        x_item_name: 'select_category',
        x_item_index: index,
        x_category_id: identifier,
        x_category_name: this.genres.find((c) => c.identifier == identifier).name
      })
    }
  }
  public trackByFn(index, item) {
    return item.identifier;
  }

  public search() {
    this.trackerEvents({
      x_item_name: 'search'
    })
    this.indicator.show();
    this.router.navigate(["/search"]);
  }

  public navigateToCat(title, identifier, contentType, index, count) {
    this.indicator.show();
    this.trackerEvent.logEvent("view_item_list", {
      item_category: "genre-" + title,
    });
    if (ProductTypeEnum[contentType]) {
      if (contentType === this.productType.Podcast || contentType === this.productType.AudioSeries) {
        this.router.navigate([`/clp/listItem/${identifier}`], {
          queryParams: { title, contentType: ProductTypeEnum[contentType] },
        });
      } else {
        this.router.navigate(["/clp", identifier], {
          queryParams: { title, contentType: ProductTypeEnum[contentType] },
        });
      }
    } else {
      this.router.navigate(["/clp", identifier], {
        queryParams: { title },
      });
    }
    this.trackerEvents({
      item_category: "genre-" + title,
      item_type: ProductTypeEnum[contentType],
      item_name: title,
      item_index: index,
      x_count_genre: count,
      x_item_name: 'go_to_genre'
    });
  }

  public goToWeb() {
    this.trackerEvents({
      x_item_name: 'old_device_go_to_web'
    });
    this.device.goToWebWithToken();
  }

  public trackerEvents(item: object) {
    this.trackerEvent.logEvent('x_clp_item_click', item);
  }
}
